import { Briefcase, Calendar, Map, Ticket, type LucideIcon } from 'lucide-react';

import { SEARCH_KINDS, type SearchKind } from '../../../api/search';
import { Permission, type PermissionString } from '../../../types/permissions';

/** Anything search can take you to: a result kind, or a workshop under its venue. */
export type SearchTargetKind = SearchKind | 'workshop';

interface SearchTargetMeta {
  /** Singular, for the row hint. */
  label: string;
  /** Plural, for the group heading and the filter chip. */
  groupLabel: string;
  icon: LucideIcon;
  href: (id: string) => string;
  /** The grants the matching list page asks for — the backend applies the same list. */
  readPermissions: readonly PermissionString[];
}

export const SEARCH_TARGETS: Record<SearchTargetKind, SearchTargetMeta> = {
  venue: {
    label: 'Venue',
    groupLabel: 'Venues',
    icon: Map,
    href: (id) => `/admin/venues/${id}`,
    readPermissions: [Permission.READ_VENUES, Permission.READ_ALL],
  },
  booking: {
    label: 'Booking',
    groupLabel: 'Bookings',
    icon: Ticket,
    href: (id) => `/admin/bookings/${id}`,
    readPermissions: [Permission.READ_BOOKINGS, Permission.READ_ALL],
  },
  privateEvent: {
    label: 'Private event',
    groupLabel: 'Private events',
    icon: Briefcase,
    href: (id) => `/admin/private-events/${id}`,
    readPermissions: [
      Permission.READ_PRIVATE_EVENTS,
      Permission.WRITE_WORKSHOPS,
      Permission.READ_ALL,
      Permission.WRITE_ALL,
    ],
  },
  // Workshop rows only appear under a venue the viewer found, so they share its grant.
  workshop: {
    label: 'Workshop',
    groupLabel: 'Workshops',
    icon: Calendar,
    href: (id) => `/admin/workshops/${id}`,
    readPermissions: [Permission.READ_VENUES, Permission.READ_ALL],
  },
};

type HasAnyPermission = (...permissions: PermissionString[]) => boolean;

export function canOpen(kind: SearchTargetKind, hasAnyPermission: HasAnyPermission): boolean {
  return hasAnyPermission(...SEARCH_TARGETS[kind].readPermissions);
}

/** The result kinds this viewer may search, in display order. */
export function searchableKinds(hasAnyPermission: HasAnyPermission): SearchKind[] {
  return SEARCH_KINDS.filter((kind) => canOpen(kind, hasAnyPermission));
}

export const MIN_SEARCH_LENGTH = 2;
export const GROUP_PREVIEW_LIMIT = 3;
export const FILTERED_LIMIT = 25;

/** Matches the API: a pasted "#12345" is searched without its "#", which doesn't count toward the minimum. */
export function isSearchableTerm(term: string): boolean {
  return term.trim().replace(/^#/, '').trim().length >= MIN_SEARCH_LENGTH;
}
